import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ChevronLeft, 
  Inbox, 
  FileText, 
  Download, 
  CheckCircle2, 
  UserPlus, 
  Save, 
  AlertCircle,
  Clock,
  Sparkles
} from 'lucide-react';
import { ENQUIRY_STATUS_CONFIG, EnquiryStatus } from '@ftt/shared';
import { Breadcrumb } from '../../components/common/Breadcrumb';

export const AdminEnquiryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [conversionSuccess, setConversionSuccess] = useState<any>(null);

  const { data: enquiry, isLoading, error } = useQuery({
    queryKey: ['admin-enquiry-detail', id],
    queryFn: () => api.get(`/enquiries/${id}`).then((res) => {
      const data = res.data.data;
      setStatus(data.status);
      setAdminNotes(data.adminNotes || '');
      return data;
    }),
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async () => {
      return api.patch(`/enquiries/${id}/status`, { status, adminNotes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enquiry-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-enquiries'] });
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    },
  });

  const convertMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/enquiries/${id}/convert`);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-enquiry-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-enquiries'] });
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      setConversionSuccess(res.data.data);
    },
  });

  if (isLoading) {
    return <div className="p-12 text-center text-xs font-mono text-slate-400">Loading submission details...</div>;
  }

  if (error || !enquiry) {
    return (
      <div className="p-12 text-center space-y-4">
        <h2 className="text-xl font-serif font-bold text-white">Enquiry Not Found</h2>
        <Link to="/admin/enquiries" className="text-xs font-bold text-brand-400 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to enquiries
        </Link>
      </div>
    );
  }

  const files = (enquiry.files as any[]) || [];

  return (
    <div className="space-y-6 max-w-5xl font-sans text-slate-900 antialiased">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          theme="light"
          items={[
            { label: 'Admin', href: '/admin' },
            { label: 'Enquiries', href: '/admin/enquiries' },
            { label: enquiry.referenceNo },
          ]}
        />
      </div>

      {/* Conversion Success Notification */}
      {conversionSuccess && (
        <div className="p-6 rounded-2xl md:rounded-3xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-3 shadow-sm">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <h3 className="font-serif text-lg font-bold text-emerald-900">Enquiry Successfully Converted!</h3>
          </div>
          <p className="text-xs leading-relaxed text-emerald-800">
            A new client profile has been provisioned and publishing project <strong>{conversionSuccess.project?.projectCode}</strong> has been initialized with all 12 milestones and transferred manuscript files.
          </p>
          {conversionSuccess.tempPassword && (
            <div className="p-3 bg-white rounded-xl font-mono text-xs text-amber-800 border border-amber-200">
              <span>New Client Account Initial Password: <strong>{conversionSuccess.tempPassword}</strong></span>
            </div>
          )}
          <div className="pt-2 flex gap-3 text-xs">
            <Link
              to={`/admin/projects`}
              className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold transition-colors shadow-sm"
            >
              View In Project Manager &rarr;
            </Link>
          </div>
        </div>
      )}

      {/* Header Card */}
      <div className="p-6 sm:p-8 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-bold text-brand-700 px-2.5 py-0.5 rounded-lg bg-brand-50 border border-brand-200">
                {enquiry.referenceNo}
              </span>
              <span className="text-xs text-slate-500 font-mono">
                {enquiry.genre}
              </span>
            </div>
            <h1 className="font-serif text-3xl font-black text-slate-900">
              {enquiry.bookTitle}
            </h1>
            <p className="text-xs text-slate-600">
              Submitted by <strong className="text-slate-900">{enquiry.fullName}</strong> ({enquiry.email}) on {new Date(enquiry.createdAt).toLocaleDateString()}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {enquiry.status !== 'CONVERTED' ? (
              <button
                onClick={() => convertMutation.mutate()}
                disabled={convertMutation.isPending}
                className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs flex items-center gap-2 shadow-md transition-colors cursor-pointer disabled:opacity-50"
              >
                <UserPlus className="w-4 h-4 text-[#FF6B4A]" />
                <span>Convert to Client &amp; Project</span>
              </button>
            ) : (
              <span className="px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 font-mono text-xs font-bold">
                ✓ Converted to Active Project
              </span>
            )}
          </div>
        </div>

        {/* Specification Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-mono">
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
            <span className="text-slate-500 block text-[10px] uppercase font-bold">Author / Organization</span>
            <span className="font-bold text-slate-900 text-sm">{enquiry.authorName}</span>
            <span className="text-slate-600 block text-[11px] mt-0.5">{enquiry.organization || 'Independent'} &bull; {enquiry.country}</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
            <span className="text-slate-500 block text-[10px] uppercase font-bold">Current Stage &amp; Words</span>
            <span className="font-bold text-slate-900 text-sm">{enquiry.stage.replace(/_/g, ' ')}</span>
            <span className="text-slate-600 block text-[11px] mt-0.5">{enquiry.wordCount ? `${enquiry.wordCount.toLocaleString()} words` : 'Word count not specified'}</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
            <span className="text-slate-500 block text-[10px] uppercase font-bold">Target Timeline</span>
            <span className="font-bold text-slate-900 text-sm">{enquiry.expectedTimeline || 'Standard timeline'}</span>
          </div>
        </div>

        {/* Requirements */}
        <div className="space-y-2 border-t border-slate-100 pt-4">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700 font-mono block">
            Requested Publishing Requirements:
          </span>
          <div className="flex flex-wrap gap-2">
            {(enquiry.requirements as string[]).map((r, idx) => (
              <span key={idx} className="px-3 py-1 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-800 font-medium">
                {r}
              </span>
            ))}
          </div>
        </div>

        {/* Additional Notes */}
        {enquiry.additionalInfo && (
          <div className="space-y-1 text-xs text-slate-700 border-t border-slate-100 pt-4 font-sans">
            <span className="font-bold uppercase text-[10px] font-mono text-slate-500 block">Author Notes / Synopsis</span>
            <p className="leading-relaxed">{enquiry.additionalInfo}</p>
          </div>
        )}
      </div>

      {/* Submitted Files */}
      <div className="p-6 sm:p-8 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-4 shadow-sm">
        <h3 className="font-serif text-lg font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#FF6B4A]" />
          <span>Attached Manuscript &amp; Proposal Files ({files.length})</span>
        </h3>

        {files.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500 font-mono bg-slate-50 rounded-xl border border-slate-200">No files were attached with this submission.</div>
        ) : (
          <div className="space-y-2">
            {files.map((file, idx) => (
              <div
                key={idx}
                className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs text-slate-700"
              >
                <div className="flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-brand-600 shrink-0" />
                  <span className="font-bold text-slate-900">{file.name}</span>
                  <span className="text-[10px] text-slate-500 font-mono">({Math.round(file.size / 1024)} KB)</span>
                </div>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3.5 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-900 font-mono text-xs font-bold flex items-center gap-1.5 border border-slate-200 shadow-xs transition-colors"
                >
                  <Download className="w-3.5 h-3.5 text-[#FF6B4A]" />
                  <span>Download</span>
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status & Internal Administrative Notes */}
      <div className="p-6 sm:p-8 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-6 shadow-sm">
        <h3 className="font-serif text-lg font-bold text-slate-900 border-b border-slate-100 pb-3">
          Acquisitions Status &amp; Committee Notes
        </h3>

        {updateSuccess && (
          <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-medium">Enquiry status updated successfully.</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
              Acquisitions Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
            >
              <option value="NEW">New</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="CONTACTED">Contacted</option>
              <option value="CONSULTATION">Consultation</option>
              <option value="PROPOSAL_SENT">Proposal Sent</option>
              <option value="CONVERTED">Converted to Client</option>
              <option value="DECLINED">Declined</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
        </div>

        <div className="text-xs">
          <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
            Internal Committee Notes (Private to Staff)
          </label>
          <textarea
            rows={4}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Enter private internal evaluation notes, reviewer feedback, or scheduling details..."
            className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-hidden focus:border-brand-500"
          />
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={() => updateStatusMutation.mutate()}
            disabled={updateStatusMutation.isPending}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-colors disabled:opacity-50 cursor-pointer"
          >
            <Save className="w-4 h-4 text-[#FF6B4A]" />
            <span>Save Status &amp; Notes</span>
          </button>
        </div>
      </div>

    </div>
  );
};
